import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { MultiSelectModule } from "primeng/multiselect";
import { SelectModule } from "primeng/select";
import { AccessRolePermissionKey } from "../../../../../domain/access-role";
import { UserDetails } from "../../../../../domain/user";
import { OrganizationService } from "../../../../../services/organization.service";

interface SelectOption {
  label: string;
  value: string | number;
}

/*
  A Formly field that renders a single- or multi-select dropdown of users in the
  currently active organization. Users are fetched from
  `OrganizationService.activeOrganization$` and sorted by their full name.

  Optional filtering:
  - `requiredAccessRolePermission`: keeps only users whose
    assigned access role grants the specified permission (via `AccessRoleDetails.has()`,
    so `canViewAllServices` implicit grants are respected). Users with no access
    role assignment are excluded.

  Example usage (single selection organization users dropdown):
  {
    key: "userId",
    type: "selectOrgUser",
    props: {
      label: {
        en: "User",
        fi: "Käyttäjä",
        sv: "Användare",
      },
      required: true,                                       // Optional
      placeholder: {
        en: "Choose one",
        fi: "Valitse yksi",
        sv: "Välj en",
      },                                                    // Optional
      description: {
        en: "Choose one of the options",
        fi: "Valitse yksi vaihtoehto",
        sv: "Välj ett alternativ",
      },                                                    // Optional
      requiredAccessRolePermission: "canApproveProcesses",  // Optional: Only show users whose role has this permission
    },
  },


  Multiselect organization users dropdown:
  {
    key: "userIds",
    type: "selectOrgUser",
    props: {
      label: {
        en: "Users",
        fi: "Käyttäjät",
        sv: "Användare",
      },
      required: true,                                       // Optional
      placeholder: {
        en: "Choose multiple",
        fi: "Valitse useita",
        sv: "Välj flera",
      },                                                    // Optional
      description: {
        en: "Choose multiple",
        fi: "Valitse useita",
        sv: "Välj flera",
      },                                                    // Optional
      multiselect: true,                                    // Optional: Enable multiselect
      requiredAccessRolePermission: "canApproveProcesses",  // Optional: Only show users whose role has this permission
    },
  },


  ////////////////////

  JSON example:

  Single selection organization users dropdown:
  {
    "key": "userId",
    "type": "selectOrgUser",
    "props": {
      "label": {
        "en": "User",
        "fi": "Käyttäjä",
        "sv": "Användare"
      },
      "required": true,                                     // Optional
      "placeholder": {
        "en": "Choose one",
        "fi": "Valitse yksi",
        "sv": "Välj en"
      },                                                    // Optional
      "description": {
        "en": "Choose one of the options",
        "fi": "Valitse yksi vaihtoehto",
        "sv": "Välj ett alternativ"
      },                                                    // Optional
      "requiredAccessRolePermission": "canApproveProcesses" // Optional: Only show users whose role has this permission
    }
  }


  Multiselect organization users dropdown:
  {
    "key": "userIds",
    "type": "selectOrgUser",
    "props": {
      "label": {
        "en": "Users",
        "fi": "Käyttäjät",
        "sv": "Användare"
      },
      "required": true,                                     // Optional
      "placeholder": {
        "en": "Choose multiple",
        "fi": "Valitse useita",
        "sv": "Välj flera"
      },                                                    // Optional
      "description": {
        "en": "Choose multiple",
        "fi": "Valitse useita",
        "sv": "Välj flera"
      },                                                    // Optional
      "multiselect": true,                                  // Optional: Enable multiselect      
      "requiredAccessRolePermission": "canApproveProcesses" // Optional: Only show users whose role has this permission
    }
  }
*/

@Component({
  selector: "app-primeng-select",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SelectModule, MultiSelectModule, TranslocoDirective],
  templateUrl: "./select.type.html",
})
export class SelectOrgUsersComponent extends FieldType<FieldTypeConfig> implements OnInit {
  public orgUsers!: UserDetails[];
  public selectOptions!: SelectOption[];
  public userLang: string;
  private destroyRef = inject(DestroyRef);

  constructor(
    private translocoService: TranslocoService,
    private organizationService: OrganizationService,
  ) {
    super();
    this.userLang = this.translocoService.getActiveLang();
  }

  ngOnInit() {
    // Subscribe reactively (no take(1)) so the dropdown updates if the active org's
    // users or access roles change while the dialog is open (e.g. after a role's
    // permissions are edited in another dialog).
    this.organizationService.activeOrganization$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((organization) => {
      if (organization) {
        let users = organization.users;

        const requiredPermission = this.props["requiredAccessRolePermission"] as AccessRolePermissionKey | undefined;
        if (requiredPermission) {
          const rolesById = new Map(organization.accessRoles.map((r) => [r.id, r]));
          users = users.filter((user) => {
            const roleAssignment = user.roleAssignments.find((x) => x.organizationId === organization.id) ?? null;
            if (roleAssignment === null) {
              return false;
            }
            return rolesById.get(roleAssignment.accessRole.id)?.has(requiredPermission) ?? false;
          });
        }

        this.selectOptions = users
          .map((user) => ({
            value: user.id,
            label: user.fullName,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, this.userLang));
      }
    });

    if (this.props.required) {
      this.formControl.setValidators([
        ...(this.formControl.validator ? [this.formControl.validator] : []),
        // Angular's built‐in req validator
        (ctrl) => {
          if (this.isMultiselect) {
            return ctrl.value == null || ctrl.value.length === 0 ? { required: true } : null;
          } else {
            return ctrl.value == null || ctrl.value === "" ? { required: true } : null;
          }
        },
      ]);
      this.formControl.updateValueAndValidity({ onlySelf: true });
    }

    // Initialize default value for multiselect
    if (this.isMultiselect && this.formControl.value === null) {
      this.formControl.setValue([]);
    }
  }

  get isMultiselect(): boolean {
    return !!this.props["multiselect"];
  }

  get errorMessage() {
    if (this.formControl.hasError("required")) {
      return (
        this.props["validation"]?.required ||
        this.translocoService.translate("app.formly-types.validationMessages.fieldRequired")
      );
    }
    return this.translocoService.translate("app.formly-types.validationMessages.invalidSelection");
  }
}
